import { useLazyQuery } from '@apollo/client';
import { useContext, useEffect, useState } from 'react';
import { Context } from '../../../../../context/AppContext';
import { MAX_SUGGESTIONS } from '../../../constants';
import debounce from '../../utils/debounce';
import { getNestedValue, isNotEmptyArray } from '../../utils/helpers';
import CategorySuggestQuery from './CategorySuggestQuery';

/**
 * Transforms fetched data object into an array of category suggestions
 * @param {Object} data The fetched data
 * @returns {Array} Array of category suggestions
 */
const transformData = data => {
  const edges = getNestedValue(data, 'categorySuggest', 'edges');
  if (!isNotEmptyArray(edges)) return [];
  return edges.map(({ node: { id, name, path } }) => ({
    id,
    name,
    path,
    suggestLabel: name,
  }));
};

/**
 * Logic for fetching and returning category suggestions
 * @returns {Object} {
 * categorySuggestions,
 * error,
 * fetchCategorySuggestions,
 * loading,
 * setCategorySuggestions,
 * topCategorySuggestion
 * }
 */
const useCategorySuggest = () => {
  const [categorySuggestions, setCategorySuggestions] = useState();
  const [topCategorySuggestion, setTopCategorySuggestion] = useState();
  const [fetch, { data, error, loading }] = useLazyQuery(CategorySuggestQuery);
  const {
    media: { mobile },
  } = useContext(Context);

  const maxSuggestions = mobile
    ? MAX_SUGGESTIONS.MOBILE.CATEGORY_SUGGESTIONS
    : MAX_SUGGESTIONS.DESKTOP.CATEGORY_SUGGESTIONS;

  /**
   * Fetch the category suggestions from the server
   * @param {string} args.query The search term to query
   * @param {bool} args.debounceFetch Should the fetch be debounced? Defaults to true
   * @returns {void}
   */
  const fetchCategorySuggestions = ({ query, debounceFetch = true }) => {
    const fetchParams = {
      variables: {
        query,
        first: maxSuggestions,
      },
    };
    if (debounceFetch) debounce(() => fetch(fetchParams));
    else fetch(fetchParams);
  };

  /**
   * Update the category suggestions whenever the fetched data is changed
   */
  useEffect(() => {
    if (!loading) {
      const hasSuggestions = isNotEmptyArray(
        getNestedValue(data, 'categorySuggest', 'edges')
      );
      if (hasSuggestions) {
        setCategorySuggestions(transformData(data));
      } else {
        setCategorySuggestions();
      }
    }
  }, [data, loading]);

  /**
   * Update the top category suggestion whenever the category suggestions are changed
   */
  useEffect(() => {
    if (isNotEmptyArray(categorySuggestions)) {
      setTopCategorySuggestion(categorySuggestions[0]);
    }
  }, [categorySuggestions]);

  return {
    categorySuggestions,
    error,
    fetchCategorySuggestions,
    loading,
    setCategorySuggestions,
    topCategorySuggestion,
  };
};

export default useCategorySuggest;
